"use client";
import type { ReactElement } from "react";
import { cn } from "@/lib/cn";

/**
 * Copy we deliberately say better than the backend does — a link-level failure
 * the lead can act on, phrased for someone who has never heard of a "token".
 * Every OTHER code renders the backend's own `message`, which is already
 * written for the lead and is the only thing that can distinguish two failures
 * sharing one code.
 *
 * Nothing generic belongs in here. `BOOKING_FAILED`, `RESCHEDULE_FAILED`,
 * `CANCEL_FAILED` and `UNEXPECTED_ERROR` used to sit in this map holding the
 * identical "Something went wrong" sentence, which is exactly what the fallback
 * below already says — so all they did was outrank a more specific message the
 * server had already sent. `RESCHEDULE_FAILED` is the sharpest case: it carries
 * two very different sentences under one code, and the difference decides
 * whether the lead should turn up to the meeting.
 */
const ERROR_MESSAGES: Record<string, string> = {
  INVALID_LINK: "This link appears to be invalid.",
  LINK_NOT_FOUND: "This booking link is not valid.",
  LINK_EXPIRED: "This link has expired. Please contact the organizer for a new one.",
  LINK_USED: "This meeting has already been booked.",
  LINK_CANCELLED: "This booking link is no longer available.",
  LINK_UNAVAILABLE: "This booking link is no longer available.",
  LINK_RESCHEDULED: "This link has been rescheduled. Please check your email for the new booking link.",
  NOT_BOOKED: "This booking has already been cancelled or rescheduled.",
  CALENDAR_UNAVAILABLE: "Temporarily unavailable. The organizer has been notified.",
  RATE_LIMITED: "Too many requests. Please try again in a moment.",
};

const GENERIC_MESSAGE = "Something went wrong. Please try again.";

/**
 * `RESCHEDULE_FAILED` means "your original time is unchanged, try again shortly"
 * and nothing else, so it is retryable.
 *
 * That is true only since kris-backend#9789 split the other outcome out as
 * `RESCHEDULE_DESYNCED` — the row moved and the calendar invite did not. While
 * both shared one code, retry had to be withheld from both: the code could not
 * distinguish them, and inviting a second move onto an already-inconsistent
 * booking is the harmful direction. `RESCHEDULE_DESYNCED` is absent here on
 * purpose, and stays absent — nothing about it is retryable until a human has
 * reconciled the row against the invite.
 */
const RETRYABLE_ERRORS = new Set([
  "CALENDAR_UNAVAILABLE",
  "BOOKING_FAILED",
  "RESCHEDULE_FAILED",
  "CANCEL_FAILED",
  "UNEXPECTED_ERROR",
  // The one link-level refusal an AE can lift while the lead is still on the
  // page. Every other LINK_* code is terminal — not found, used, cancelled,
  // expired — and a retry there can only re-fail, which is why they stay out.
  // Without this the lead's only way back was a hard reload they had no reason
  // to attempt, since nothing on screen suggested the answer could change.
  "LINK_PAUSED",
  // Its own copy already says "Please try again in a moment" and then gave the
  // lead nothing to try again WITH. The button issues the same request a
  // reload would, so it adds no load the lead could not already generate.
  "RATE_LIMITED",
]);

type Props = {
  errorCode: string;
  /**
   * The `message` the backend sent with this failure. Used whenever we have no
   * deliberately better sentence of our own — which is how a code this page has
   * never heard of still says something true instead of "Something went wrong".
   * The backend's code list grows; this page's map does not grow with it.
   */
  serverMessage?: string;
  onRetry?: () => void;
  className?: string;
};

export function ErrorState({
  errorCode,
  serverMessage,
  onRetry,
  className,
}: Props): ReactElement {
  // `||`, not `??`, on the server message: an error body carrying `message: ""`
  // has told us nothing, and rendering an empty heading is worse than the
  // generic sentence.
  const message =
    ERROR_MESSAGES[errorCode] ?? (serverMessage?.trim() || GENERIC_MESSAGE);
  const canRetry = RETRYABLE_ERRORS.has(errorCode) && onRetry;

  return (
    <div className={cn("flex flex-col items-center justify-center py-16 text-center", className)}>
      <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-50">
        <svg className="h-8 w-8 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
        </svg>
      </div>
      <h2 className="mb-2 text-lg font-semibold text-neutral-800">{message}</h2>
      {canRetry && (
        <button
          onClick={onRetry}
          className="mt-4 rounded-full bg-[#029391] px-6 py-2 text-sm font-medium text-white hover:bg-[#016868] transition-colors"
        >
          Try Again
        </button>
      )}
    </div>
  );
}
